import { useEffect, useMemo, useRef } from 'react';
import { track, EVENTS } from '../services/analytics';
import { LAYOUT, answerFor, landingsFor } from '../content/adLandings';

// A paid landing on a college page (#4088, the Deadline-Season Ads Plan).
// The plan pauses an ad group when 70% or more of its landings leave within
// 10 seconds after 50 clicks; a session's span cannot say that (one page view
// and a closed tab reads as 0 seconds whether they read for a minute or not),
// so the page says it: ad_landing_seen once on load, ad_landing_stayed once if
// the page is still open and visible STAY_MS later. Ids and tags only: the
// school's slug and KB id, the campaign tag (ours, written into the ad's URL),
// the source tag, and whether an ad click id was present (never the id).

export const STAY_MS = 10000;
const TAG = /^[a-z0-9][a-z0-9._-]{0,79}$/;
export const PLAN_PREFIX = 'deadline-';

/** The landing's tags from a URL query string, or null when it is not a
 * paid landing (no plan campaign and no ad click id). */
export function adLandingOf(search) {
    let params;
    try {
        params = new URLSearchParams(search || '');
    } catch {
        return null;
    }
    const tag = (k) => {
        const v = String(params.get(k) || '').trim().toLowerCase();
        return TAG.test(v) ? v : null;
    };
    const campaign = tag('utm_campaign');
    const paidClick = ['gclid', 'gbraid', 'wbraid'].some((k) => Boolean(params.get(k)));
    const plan = campaign && campaign.startsWith(PLAN_PREFIX) ? campaign : null;
    if (!plan && !paidClick) return null;
    return { campaign: plan, source: tag('utm_source'), paid_click: paidClick };
}

// A paid visitor's first screen (#4476, founder pick A): the ad's question,
// its answer and the stand card come first. Paid means a paid medium or one
// of the plan's campaigns; a bare click id without utm tags is not enough
// (every live ad carries both). The rule runs twice: here, after the app
// starts (it also follows client-side navigation), and in index.html's head
// before the prerendered page paints, which marks <html data-ad-landing> so
// the page's own CSS draws the paid layout from the first frame. The two
// copies are pinned equal by adLandings.test.jsx.
export const PAID_MEDIA = ['cpc', 'paid', 'paid_social'];
export const AD_LANDING_ATTR = 'data-ad-landing';

/** {term, medium, campaign, source} for a paid landing, else null. `term`
 * is the utm_term when it is a clean tag, '' otherwise. */
export function paidLandingOf(search) {
    let params;
    try {
        params = new URLSearchParams(search || '');
    } catch {
        return null;
    }
    const raw = (k) => String(params.get(k) || '').trim().toLowerCase();
    const medium = raw('utm_medium');
    const campaign = raw('utm_campaign');
    if (!PAID_MEDIA.includes(medium) && campaign.indexOf(PLAN_PREFIX) !== 0) return null;
    const tag = (v) => (TAG.test(v) ? v : null);
    return { term: tag(raw('utm_term')) || '', medium: tag(medium), campaign: tag(campaign), source: tag(raw('utm_source')) };
}

/** Fires the two events for one college page view. `search` is the URL's
 * query string at load; a client-side visit with no ad tags fires nothing. */
export default function useAdLanding(slug, schoolId, search) {
    useEffect(() => {
        const landing = adLandingOf(search);
        if (!landing || !slug) return undefined;
        const props = { slug, school_id: schoolId || null, ...landing };
        track(EVENTS.AD_LANDING_SEEN, props);
        let done = false;
        const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
        const timer = setTimeout(() => {
            if (done || hidden()) return;
            done = true;
            track(EVENTS.AD_LANDING_STAYED, props);
        }, STAY_MS);
        // Leaving the tab before the 10 seconds is leaving.
        const onHide = () => { if (hidden()) { done = true; clearTimeout(timer); } };
        if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onHide);
        return () => {
            done = true;
            clearTimeout(timer);
            if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onHide);
        };
    }, [slug]);   // eslint-disable-line react-hooks/exhaustive-deps
}

const orientation = () => {
    try {
        return window.matchMedia('(orientation: landscape)').matches ? 'landscape' : 'portrait';
    } catch {
        return null;
    }
};

/** The paid first screen's landing for this page, or null: {layout, answer,
 * term, medium, campaign, source}. Pure in render (it changes no markup, so
 * the prerendered page hydrates as built); the effect keeps <html
 * data-ad-landing> in step with the URL across client-side navigation and
 * counts the screen once. `college` is the page's data (data/colleges). */
export function usePaidLanding(college, search) {
    const slug = college ? college.slug : null;
    const schoolId = college ? college.kbId : null;
    const landing = useMemo(() => {
        const paid = college && landingsFor(college).length ? paidLandingOf(search) : null;
        return paid ? { layout: LAYOUT, answer: answerFor(college, paid.term), ...paid } : null;
    }, [college, search]);
    const counted = useRef(null);
    useEffect(() => {
        const root = typeof document !== 'undefined' ? document.documentElement : null;
        if (!root) return undefined;
        if (!landing) { root.removeAttribute(AD_LANDING_ATTR); return undefined; }
        root.setAttribute(AD_LANDING_ATTR, landing.term);
        const once = `${slug}?${search}`;
        if (counted.current !== once) {
            counted.current = once;
            track(EVENTS.AD_LANDING_BLOCK_SHOWN, {
                slug, school_id: schoolId || null, layout: landing.layout, answer: landing.answer,
                term: landing.term || null, campaign: landing.campaign, source: landing.source,
                medium: landing.medium, orientation: orientation(),
            });
        }
        return () => root.removeAttribute(AD_LANDING_ATTR);
    }, [landing]);   // eslint-disable-line react-hooks/exhaustive-deps
    return landing;
}
